import { Grid2 } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMakeApiCalls } from "../../../../../../Hooks/useMakeApiCalls";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import styles from "./SshStyles.module.scss";

import PageLoading from "../../../../../../GlobalComponents/PageLoading/PageLoading";

import { v4 as uuidv4 } from "uuid";

// SockJS and STOMP are optional — feature degrades gracefully when unavailable
let SockJS = null;
let StompClient = null;
try {
  SockJS = require("sockjs-client");
} catch (_) {}
try {
  StompClient = require("@stomp/stompjs").Client;
} catch (_) {}

export default function SshPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const domainName = searchParams.get("domainName");
  const [stagingDomain, setStagingDomain] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(true);
  const orgId = params.orgId;
  const location = useLocation();
  const [curDir, setCurDir] = useState("/var/www/wordpress");

  const siteId = parseInt(params.siteId);

  const [copied, setCopied] = useState(false);
  const [command, setCommand] = useState("");
  const [terminalHistory, setTerminalHistory] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);

  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isCommandRunning, setIsCommandRunning] = useState(false);
  const [sessionId] = useState(uuidv4());
  const stompClient = useRef(null);
  const terminalRef = useRef(null);

  useMakeApiCalls(() => {
    getStore().dispatch({ type: Actions.API_USER_DETAILS });
    getStore().dispatch({
      type: Actions.API_GET_SELECTED_SITE_DETAILS,
      data: { siteId: siteId },
      meta: {
        onSuccess: (response) => {
          setIsPageLoading(false);
          if (response && response.stagingDomainsList) {
            response.stagingDomainsList.forEach((stagingDomain) => {
              if (stagingDomain.domainName === domainName) {
                setStagingDomain(stagingDomain);
              }
            });
          }
        },
        onError: () => {
          setIsPageLoading(false);
        },
      },
    });
  });

  const siteDetails = useSelector((state) => state.sitesInfo.selectedSite);

  const handleCopyToClipboard = () => {
    const textToCopy = terminalHistory
      .map((entry) => `$ ${entry.command}\n${entry.output}`)
      .join("\n");

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopied("Terminal output copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  };

  const handleExecuteCommand = (inputCommand) => {
    setIsExecuting(true);

    getStore().dispatch({
      type: Actions.API_EXECUTE_COMMAND,
      data: { command: inputCommand, siteId: siteId },
      meta: {
        onSuccess: (data) => {
          if (data) {
            setCommand("");
            setIsExecuting(false);
            setTerminalHistory((prevHistory) => [
              ...prevHistory,
              { command: inputCommand, output: `${data}` },
            ]);
          }
        },
        onError: (data) => {
          if (data) {
            setCommand("");
            setIsExecuting(false);
            setTerminalHistory((prevHistory) => [
              ...prevHistory,
              { command: inputCommand, output: `${data}` },
            ]);
          }
        },
      },
    });
  };

  let token = localStorage.getItem("accessToken");

  useEffect(() => {
    // WebSocket connection requires sockjs-client and @stomp/stompjs.
    // When those packages are unavailable the terminal renders in offline mode
    // (isConnected stays false) so the UI is still testable.
    if (!SockJS || !StompClient) return;

    const socket = new SockJS(
      `${process.env.REACT_APP_BACK_END_API_BASE_URL}/terminal-websocket?token=${token}`
    );
    stompClient.current = new StompClient({
      webSocketFactory: () => socket,
      debug: function (str) {},
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

    stompClient.current.onConnect = (frame) => {
      setIsConnected(true);

      stompClient.current.subscribe(
        "/queue/specific-user/" + sessionId,
        (message) => {
          try {
            const response = JSON.parse(message.body);

            setCurDir(response.curDir.split("/").pop());

            setHistory((prev) => [
              ...prev,
              {
                type: message.isError ? "error" : "output",
                content: response.content,
              },
            ]);

            if (
              response.content.includes("Command completed") ||
              response.content.includes("Command was cancelled") ||
              response.content.includes("Command execution timed out") ||
              response.content.includes("Error executing command") ||
              response.content.includes("Failed to execute command")
            ) {
              setIsCommandRunning(false);
            }
          } catch (error) {
            console.error("Error parsing message:", error);
            setHistory((prev) => [
              ...prev,
              {
                type: "error",
                content: "Error parsing message: " + error.message,
              },
            ]);
            setIsCommandRunning(false);
          }
        }
      );
    };

    stompClient.current.onStompError = (frame) => {
      setHistory((prev) => [
        ...prev,
        {
          type: "error",
          content: `Connection error: ${frame.headers["message"]}`,
        },
      ]);
      setIsConnected(false);
      setIsCommandRunning(false);
    };

    stompClient.current.onWebSocketError = (error) => {
      setHistory((prev) => [
        ...prev,
        {
          type: "error",
          content: `WebSocket error: Connection failed`,
        },
      ]);
      setIsConnected(false);
      setIsCommandRunning(false);
    };

    stompClient.current.activate();

    return () => {
      if (stompClient.current) {
        stompClient.current.deactivate();
      }
    };
  }, [sessionId]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!input.trim()) return;

    setHistory((prev) => [
      ...prev,
      {
        type: "command",
        content: `$ ${input}`,
      },
    ]);

    if (isConnected && stompClient.current) {
      stompClient.current.publish({
        destination: "/app/execute",
        body: JSON.stringify({
          command: input,
          sessionId: sessionId,
          domainName: domainName,
          token: token,
          withDir: true,
        }),
      });

      setIsCommandRunning(true);
    } else {
      setHistory((prev) => [
        ...prev,
        {
          type: "error",
          content: "Not connected to server. Please try again later.",
        },
      ]);
    }

    setInput("");
  };

  const handleCancel = () => {
    if (isConnected && isCommandRunning && stompClient.current) {
      stompClient.current.publish({
        destination: "/app/cancel",
        body: JSON.stringify({
          sessionId: sessionId,
        }),
      });

      setHistory((prev) => [
        ...prev,
        {
          type: "system",
          content: "Cancelling command...",
        },
      ]);
    }
  };

  return (
    <Grid2>
      <Grid2 className={styles.mainContainer}>
        <Grid2 className={styles.nameContainer}>
          <Grid2 className={styles.label}>Sites </Grid2>
          <Grid2 className={styles.siteName}>
            <li />
            {domainName}
          </Grid2>
          {copied && <Grid2 className={styles.copieLabel}>{copied}</Grid2>}
        </Grid2>

        <div className={styles["terminal-container"]}>
          <div className={styles["terminal-header"]}>
            <div className={styles["terminal-status"]}>
              Status: {isConnected ? "Connected" : "Disconnected"}
            </div>
            {isCommandRunning && (
              <button
                className={styles["terminal-cancel-button"]}
                onClick={handleCancel}
                title="Cancel running command"
              >
                Cancel
              </button>
            )}
          </div>
          <div className={styles["terminal-output"]} ref={terminalRef}>
            {history.map((item, index) => (
              <div key={index} className={`terminal-line ${item.type}`}>
                {item.content}
              </div>
            ))}
            {isCommandRunning && (
              <div className={styles["terminal-line system"]}>
                <span className={styles["terminal-spinner"]}></span> Command
                running...
              </div>
            )}
          </div>
          <form
            onSubmit={handleSubmit}
            className={styles["terminal-input-container"]}
          >
            <span className={styles["terminal-prompt"]}>{curDir} $</span>
            <input
              type="text"
              value={input}
              onChange={handleInputChange}
              className={styles["terminal-input"]}
              placeholder="Type command and press Enter"
              disabled={isCommandRunning}
              autoFocus
            />
          </form>
        </div>
      </Grid2>
    </Grid2>
  );
}
